import { mat4 } from "gl-matrix";
import { isMesh } from "../glsl/typeMapping";
import { Shader } from "../shader/shader";
import { PBR } from "./materials/pbr";
const identityMatrix = mat4.create();
mat4.identity(identityMatrix);
export class Model extends Shader {
    constructor(synth, props = {}) {
        props.vertex = props.vertex ?? { value: null };
        if (!props.fragment && props.material)
            props.fragment = new PBR(synth, props.material);
        props.fragment = props.fragment /*material*/ ?? { value: () => "vec4(1.)" };
        props.cullFace = props.cullFace ?? "back";
        props.depthTest = props.depthTest ?? true;
        props.blendMode = props.blendMode ?? null;
        super(synth, props);
        this.material = props.material;
        this.mesh = props.mesh;
        this.camera(props.camera);
        this.modelMatrix = props.modelMatrix ?? mat4.clone(identityMatrix);
        //Object.assign(Model.prototype, matrixTransformation(this.modelMatrix, this))
        this.defines["HAS_MODEL_MATRIX"] = true;
        this._uniforms.push({
            name: "modelMatrix",
            type: "mat4",
            value: this.modelMatrix,
        });
        this.defines[isMesh] = true;
    }
    pbr() {
        return new PBR(this.synth, this.material);
    }
    ////////////////////
    vertexColor(...args) {
        if (!this.VAO)
            return this;
        const len = this.VAO._elements || this.VAO._count;
        const colors = new Float32Array(len * 4);
        for (let i = 0; i < colors.length; i += 4) {
            colors[i + 0] = args[(i + 0) % args.length];
            colors[i + 1] = args[(i + 1) % args.length];
            colors[i + 2] = args[(i + 2) % args.length];
            colors[i + 3] = args[(i + 3) % args.length];
        }
        this.VAO.attrs({
            vertexColor: {
                data: colors,
                size: 4,
            },
        });
        return this;
    }
    wireframe(w = true) {
        this.VAO._primitive = w ? this.synth.renderer.gl.LINE_LOOP : this.synth.renderer.gl.TRIANGLES;
        return this;
    }
    primitive(primitive = this.synth.renderer.gl
        .TRIANGLES) {
        if (!(primitive in ["triangles", "lines", "points", "line_strip", "triangle_strip"]) && typeof primitive !== "number")
            return this;
        const primitiveMap = {
            triangles: this.synth.renderer.gl.TRIANGLES,
            lines: this.synth.renderer.gl.LINES,
            points: this.synth.renderer.gl.POINTS,
            line_strip: this.synth.renderer.gl.LINE_STRIP,
            triangle_strip: this.synth.renderer.gl.TRIANGLE_STRIP,
        };
        this.VAO._primitive = typeof primitive === "string" ? primitiveMap[primitive] : primitive;
        return this;
    }
    point(pointSize = 1) {
        if (pointSize) {
            this.VAO._primitive = this.synth.renderer.gl.POINTS;
            this.uniform({
                pointSize,
            });
            this.defines["HAS_POINTSIZE"] = true;
        }
        else {
            this.VAO._primitive = this.synth.renderer.gl.TRIANGLES;
            this.uniform({
                pointSize: null,
            });
            delete this.defines["HAS_POINTSIZE"];
        }
        return this;
    }
    useTexCoord(enabled = true) {
        if (enabled) {
            if (this.mesh && this.VAO && !this.VAO.hasAttr("texCoord") && this.mesh.texCoord) {
                this.VAO.attrs({
                    texCoord: this.mesh.texCoord,
                });
            }
        }
        else {
            this.VAO?.deleteAttr("texCoord");
            delete this.defines["HAS_TEXCOORD"];
        }
        return this;
    }
    ////////////////////
    camera(camera = this.synth._camera) {
        if (camera) {
            this.defines["HAS_CAMERA"] = true;
            // l'aggiornamento della matrice di proiezione è pleonastico siccome la matrice non viene copiata
            this.uniform(camera.uniforms);
            // Potrei usare shaderUniform, ma dovrei aggiornare Generator da Uniform[] a ShaderUniforms
        }
        else {
            this.uniform({
                viewMatrix: null,
                projectionMatrix: null,
            });
        }
        return this;
    }
    out(texture = this.synth.defaultOutput) {
        return super.out(texture);
    }
    ////////////////////
    buffer(name, buffer) {
        const buffers = typeof name === "string" ? { [name]: buffer } : name;
        if (this.VAO)
            this.VAO.attrs(buffers);
        return this;
    }
    indices(...args) {
        if (!this.VAO)
            return this;
        const len = this.VAO._elements || this.VAO._count;
        const indices = new Uint16Array(len);
        for (let i = 0; i < indices.length; i++) {
            indices[i] = args[i % args.length];
        }
        this.VAO.cells(indices);
        return this;
    }
    clear() {
        this.modelMatrix = mat4.clone(identityMatrix);
        this.camera(null);
        return super.clear();
    }
    instances(count = 0) {
        this.VAO.instances(count);
        this.define("INSTANCES", count);
        return this;
    }
    clone(props = {}, generated = false) {
        this.compile();
        return new Model(this.synth, {
            name: this.name + "_copy",
            fragment: generated ? this.fragmentGenerated : this.fragmentSource,
            vertex: generated ? this.vertexGenerated : this.vertexSource,
            uniforms: this._uniforms.slice(),
            defines: { ...this.defines },
            varyings: { ...this._varyings },
            autoClear: this._autoClear,
            width: this.width,
            height: this.height,
            precision: this.precision,
            vao: this.VAO ? this.VAO.clone() : null,
            ...props,
        });
    }
}
